import dayjs from 'dayjs';

/**
 * Formats a given number into Lakhs with up to 2 decimal places and the Rupee symbol.
 * If less than 1 lakh, it just shows ₹.
 */
export const formatCurrencyLakhs = (value) => {
  if (value === null || value === undefined || isNaN(value)) return value;
  const num = Number(value);
  const absNum = Math.abs(num);
  if (absNum >= 100000) {
    return `₹${(num / 1000000).toFixed(2)} M`;
  }
  if (absNum >= 1000) {
    return `₹${(num / 1000).toFixed(1)} K`;
  }
  return `₹${num.toFixed(2)}`;
};

/**
 * Formats a currency value for tooltips.
 * If >= 1L (100,000), formats in Million (e.g. ₹ 0.15 M), else formats using standard Indian locale format (e.g. ₹95,000.00).
 */
export const formatTooltipValue = (value, minimumFractionDigits = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  const num = Number(value);
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (absNum >= 100000) {
    return `${sign}₹ ${(absNum / 1000000).toFixed(2)} M`;
  }
  return `${sign}₹${absNum.toLocaleString('en-IN', { minimumFractionDigits, maximumFractionDigits: minimumFractionDigits })}`;
};

/**
 * Formats a date string into "DD-MMM-YYYY" (e.g. 01-Mar-2026) for filters.
 */
export const formatDateFilter = (dateStr) => {
  if (!dateStr) return '';
  return dayjs(dateStr).format('DD-MMM-YYYY');
};

/**
 * Formats a date string into "MMM-YY" (e.g. Mar-26) for charts.
 */
export const formatChartDateAxis = (dateStr) => {
  if (!dateStr) return '';
  // Show "DD-MMM" (e.g. 01-Apr) as requested by user
  return dayjs(dateStr).format('DD-MMM');
};

/**
 * Formats a number using K or M notation.
 */
export const formatNumberKM = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  const num = Number(value);
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (absNum >= 1000000) {
    return `${sign}${(absNum / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (absNum >= 1000) {
    return `${sign}${(absNum / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return num.toString();
};

/**
 * Formats a currency value using Rupee symbol and K or M notation.
 */
export const formatCurrencyKM = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  const num = Number(value);
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (absNum >= 1000000) {
    return `${sign}₹${(absNum / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (absNum >= 1000) {
    return `${sign}₹${(absNum / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return `${sign}₹${absNum.toFixed(absNum % 1 === 0 ? 0 : 2)}`;
};

/**
 * Sorts tooltip items in descending order (highest value first).
 */
export const tooltipSorter = (item) => {
  const v = Number(item.value);
  return isNaN(v) ? 0 : -v;
};

/**
 * Aggregates chart data for 'hourly' or '15min' views across multiple dates.
 * Fixes X-Axis to exactly 24 or 96 blocks.
 */
export const aggregateChartData = (data, granularity, avgKeys = []) => {
  if (!data || data.length === 0) return data;
  if (granularity !== 'hourly' && granularity !== '15min') return data;

  let minMins = 0;
  let maxMins = 24 * 60 - 1;

  const encounteredMins = [];
  data.forEach(item => {
    if (!item.period) return;
    let timeStr;
    if (typeof item.period === 'string' && item.period.includes(' ')) {
      timeStr = item.period.split(' ')[1];
    } else if (typeof item.period === 'string' && item.period.includes(':')) {
      timeStr = item.period;
    } else {
      return;
    }
    
    let mins;
    if (granularity === 'hourly') {
      const h = parseInt(timeStr.split(':')[0], 10);
      if (!isNaN(h)) mins = h * 60;
    } else {
      const parts = timeStr.split(':');
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!isNaN(h) && !isNaN(m)) mins = h * 60 + m;
    }
    if (mins !== undefined) {
      encounteredMins.push(mins);
    }
  });

  if (encounteredMins.length > 0) {
    minMins = Math.min(...encounteredMins);
    maxMins = Math.max(...encounteredMins);
  }

  const grouped = {};
  
  if (granularity === 'hourly') {
    const startHour = Math.floor(minMins / 60);
    const endHour = Math.floor(maxMins / 60);
    for (let i = startHour; i <= endHour; i++) {
      const h = String(i).padStart(2, '0');
      grouped[h] = { period: h, _counts: {} };
    }
  } else if (granularity === '15min') {
    for (let mins = minMins; mins <= maxMins; mins += 15) {
      const h = String(Math.floor(mins / 60)).padStart(2, '0');
      const m = String(mins % 60).padStart(2, '0');
      const timeStr = `${h}:${m}`;
      const block_number = Math.floor(mins / 15) + 1;
      grouped[timeStr] = { period: timeStr, block_number, _counts: {} };
    }
  }

  // Get all numeric keys from data to aggregate
  const keysToAggregate = new Set();
  data.forEach(item => {
    Object.keys(item).forEach(k => {
      if (k !== 'period' && typeof item[k] === 'number') {
        keysToAggregate.add(k);
      }
    });
  });

  const keysArray = Array.from(keysToAggregate);

  Object.values(grouped).forEach(g => {
    keysArray.forEach(k => g[k] = 0);
  });

  data.forEach(item => {
    if (!item.period) return;
    let timeStr;
    if (typeof item.period === 'string' && item.period.includes(' ')) {
      timeStr = item.period.split(' ')[1];
    } else if (typeof item.period === 'string' && item.period.includes(':')) {
      timeStr = item.period;
    } else {
      return; 
    }

    if (granularity === 'hourly') {
      timeStr = timeStr.split(':')[0];
    }

    if (grouped[timeStr]) {
      if (!grouped[timeStr].date) {
        if (typeof item.period === 'string' && item.period.includes(' ')) {
          grouped[timeStr].date = item.period.split(' ')[0];
        } else if (item.date) {
          grouped[timeStr].date = item.date;
        }
      }
      keysArray.forEach(k => {
        if (typeof item[k] === 'number') {
          grouped[timeStr][k] += item[k];
          grouped[timeStr]._counts[k] = (grouped[timeStr]._counts[k] || 0) + 1;
        }
      });
    }
  });

  // Apply averages where needed
  Object.values(grouped).forEach(g => {
    avgKeys.forEach(k => {
      if (keysArray.includes(k) && g._counts[k] > 0) {
        g[k] = g[k] / g._counts[k];
      }
    });
    delete g._counts;
  });

  const sortedKeys = Object.keys(grouped).sort();
  return sortedKeys.map(k => grouped[k]);
};

export const formatTableData = (data, granularity) => {
  if (!data) return [];
  return data.map(item => {
    let date = item.date;
    let time_block = item.time_block;
    let block_number = item.block_number;
    let hour = item.hour;

    const periodStr = item.period;
    if (typeof periodStr === 'string') {
      if (periodStr.includes(' ')) {
        const parts = periodStr.split(' ');
        if (!date) date = parts[0];
        const timePart = parts[1];
        if (timePart.includes(':')) {
          if (!time_block) time_block = timePart;
          if (!hour) hour = timePart.split(':')[0];
        }
      } else if (periodStr.includes(':')) {
        if (!time_block) time_block = periodStr;
        if (!hour) hour = periodStr.split(':')[0];
      } else if (granularity === 'hourly') {
        if (!hour) hour = periodStr;
      } else {
        if (!date) date = periodStr;
      }
    }

    if (time_block && time_block.includes(' - ')) {
      time_block = time_block.split(' - ')[0];
    }

    if (!block_number && time_block && time_block.includes(':')) {
      const parts = time_block.split(':');
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!isNaN(h) && !isNaN(m)) {
        block_number = Math.floor((h * 60 + m) / 15) + 1;
      }
    }

    if (!date) {
      if (granularity === 'hourly' || granularity === '15min') {
        date = '—';
      } else {
        date = periodStr || '—';
      }
    }

    return {
      ...item,
      date,
      time_block: time_block || '—',
      block_number: block_number || '—',
      hour: hour || '—'
    };
  });
};

export const getTableColumns = (granularity, customColumns) => {
  const is15Min = granularity === '15min' || granularity === '15min-raw';
  const isHourly = granularity === 'hourly';
  
  const leadCols = [];
  if (is15Min) {
    leadCols.push(
      { title: 'Date', dataIndex: 'date', key: 'date', width: 110 },
      { title: 'Time Block', dataIndex: 'time_block', key: 'time_block', width: 100 },
      { title: 'Block Number', dataIndex: 'block_number', key: 'block_number', width: 100 }
    );
  } else if (isHourly) {
    leadCols.push(
      { title: 'Date', dataIndex: 'date', key: 'date', width: 110 },
      { title: 'Hour', dataIndex: 'hour', key: 'hour', width: 80 }
    );
  } else {
    leadCols.push(
      { title: 'Date', dataIndex: 'date', key: 'date', width: 110 }
    );
  }
  
  return [...leadCols, ...customColumns];
};


